import api from './api'

/**
 * Checkout público: comprar sin tener cuenta.
 *
 * Del cuerpo sólo viaja **qué** y **cuánto**: el precio, los descuentos y los
 * totales los pone el servidor contra el catálogo. Si el precio viajara desde
 * aquí, cualquiera compraría a un peso cambiando el JSON.
 */

export interface CheckoutLineType {
  productId: number
  variantId?: number | null
  quantity: number
}

export interface CheckoutBuyerType {
  name: string
  email: string
  phone?: string
  document?: string
}

export interface CheckoutShippingType {
  address?: string
  city?: string
  state?: string
  notes?: string
}

export interface CheckoutPayloadType {
  items: CheckoutLineType[]
  buyer: CheckoutBuyerType
  shipping?: CheckoutShippingType | null
  notes?: string | null
  /** Del navegador: un doble clic no debe acabar en dos pedidos. */
  idempotencyKey?: string
}

export type PublicOrderStatusType = 'pending' | 'paid' | 'cancelled' | 'refunded' | 'expired'

/** Lo único que se le cuenta a un comprador de su propio pedido. */
export interface PublicOrderType {
  number: string
  status: PublicOrderStatusType
  currency: string
  total: number
  createdAt?: string | null
  paidAt?: string | null
}

/** Lo que empuja el SSE cuando cambia algo. */
export interface OrderStatusEventType {
  orderNumber?: string
  number?: string
  status: PublicOrderStatusType
  paymentStatus?: string | null
  failureReason?: string | null
  total?: number
  currency?: string
}

export const checkoutService = {
  /** Crea el pedido y devuelve a dónde mandar al comprador a pagar. */
  async create(empresa: string, payload: CheckoutPayloadType) {
    const { data } = await api.post(`/public/checkout/${empresa}`, payload)
    return data.data as { order: PublicOrderType; checkoutUrl: string | null }
  },

  /**
   * Estado del pedido. El backend, si sigue pendiente, le pregunta a la
   * pasarela antes de contestar, así que esto vale también cuando el aviso
   * todavía no ha llegado.
   */
  async status(empresa: string, numero: string) {
    const { data } = await api.get(`/public/checkout/${empresa}/${numero}`)
    return data.data as PublicOrderType
  },

  /**
   * Escucha en vivo el estado del pedido.
   *
   * Es SSE, no un sondeo desde el navegador: el servidor empuja el cambio en
   * cuanto la pasarela avisa. Devuelve cómo cerrar la conexión — hay que
   * llamarlo en `onCleanup`, o queda una conexión abierta por cada visita.
   */
  streamStatus(empresa: string, numero: string, onStatus: (event: OrderStatusEventType) => void, onError?: () => void): () => void {
    const base = api.defaults.baseURL ?? '/api'
    const source = new EventSource(`${base}/public/checkout/${empresa}/${numero}/stream`)

    source.addEventListener('status', event => {
      try {
        onStatus(JSON.parse((event as MessageEvent).data) as OrderStatusEventType)
      } catch {
        // Un mensaje ilegible no puede tumbar la página: se ignora y se sigue
        // escuchando; el estado real siempre se puede recuperar con `status()`.
      }
    })

    source.onerror = () => {
      // El navegador reintenta solo. Se avisa para poder caer al sondeo manual
      // si la conexión no se recupera.
      onError?.()
    }

    return () => source.close()
  }
}

export default checkoutService
